import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, RectangleStackIcon, PencilIcon, ChevronLeftIcon, PlusIcon, SparklesIcon, DocumentArrowUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { deckService } from '../services/deckService';
import quizService from '../services/quizService';
import { Deck } from '../types';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import toast from 'react-hot-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import FileUploadModal from './FileUploadModal';

interface QuizCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onQuizCreated?: () => void;
  onUpgradePrompt?: () => void;
}

interface AIQuizRequest {
  topic: string;
  description: string;
  questionCount: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  language: 'portuguese' | 'english' | 'spanish';
  additionalInfo?: string;
  essayMinWords?: number;
}

const QuizCreationModal: React.FC<QuizCreationModalProps> = ({
  isOpen,
  onClose,
  onQuizCreated,
  onUpgradePrompt,
}) => {
  const navigate = useNavigate();
  const { checkLimit, isUpgradeRequired, subscription } = useSubscription();
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [step, setStep] = useState<'choice' | 'select-deck' | 'custom-basic-info' | 'ai' | 'file'>('choice');
  const [aiSubStep, setAiSubStep] = useState(1); // Track AI form sub-steps (1-4)
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(false);
  const [quizCount, setQuizCount] = useState(0);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  
  // Custom quiz basic info
  const [customQuizTitle, setCustomQuizTitle] = useState('');
  const [customQuizDescription, setCustomQuizDescription] = useState('');
  const [customQuizTags, setCustomQuizTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  // AI quiz form state
  const [aiForm, setAIForm] = useState<AIQuizRequest>({
    topic: '',
    description: '',
    questionCount: 15,
    difficulty: 'intermediate',
    language: 'portuguese',
    additionalInfo: ''
  });

  // Question types state (selected types)
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState({
    multipleChoice: true,
    trueFalse: false,
    fillBlank: false,
    essay: false
  });

  // Essay configuration
  const [essayMinWords, setEssayMinWords] = useState(30);

  useEffect(() => {
    if (isOpen) {
      loadQuizCount();
      // Reset question count if free user and value is too high
      if (subscription?.subscriptionTier === 'free' && aiForm.questionCount > 15) {
        setAIForm({ ...aiForm, questionCount: 15 });
      }
    }
  }, [isOpen, subscription]);

  const loadQuizCount = async () => {
    try {
      const quizzes = await quizService.getQuizzes();
      setQuizCount(quizzes.length);
    } catch (error) {
      console.error('Error loading quiz count:', error);
    }
  };

  useEffect(() => {
    if (isOpen && step === 'select-deck') {
      loadDecks();
    }
  }, [isOpen, step]);

  const loadDecks = async () => {
    try {
      setLoading(true);
      const decksData = await deckService.getDecks();
      setDecks(decksData);
    } catch (error) {
      console.error('Error loading decks:', error);
      toast.error('Erro ao carregar decks');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('choice');
    setAiSubStep(1);
    setCustomQuizTitle('');
    setCustomQuizDescription('');
    setCustomQuizTags([]);
    setNewTag('');
    setShowFileUploadModal(false);
    setAIForm({
      topic: '',
      description: '',
      questionCount: 15,
      difficulty: 'intermediate',
      language: 'portuguese',
      additionalInfo: ''
    });
    setSelectedQuestionTypes({
      multipleChoice: true,
      trueFalse: false,
      fillBlank: false,
      essay: false
    });
    onClose();
  };

  const handleAIQuiz = () => {
    setStep('ai');
  };

  const handleAISubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!aiForm.topic.trim() || !aiForm.description.trim()) {
      toast.error('Tópico e descrição são obrigatórios');
      return;
    }

    // Validate at least one question type is selected
    const hasSelectedType = selectedQuestionTypes.multipleChoice || selectedQuestionTypes.trueFalse || 
                           selectedQuestionTypes.fillBlank || selectedQuestionTypes.essay;
    if (!hasSelectedType) {
      toast.error('Selecione pelo menos um tipo de questão');
      return;
    }

    // Check quiz limit
    if (!checkLimit('quiz', quizCount)) {
      if (onUpgradePrompt) onUpgradePrompt();
      return;
    }

    setLoading(true);
    try {
      // Distribute questions among selected types
      const selectedTypes = Object.entries(selectedQuestionTypes).filter(([_, selected]) => selected);
      const baseCount = Math.floor(aiForm.questionCount / selectedTypes.length);
      const remainder = aiForm.questionCount % selectedTypes.length;
      
      const distributedTypes = {
        multipleChoice: selectedQuestionTypes.multipleChoice ? baseCount + (remainder > 0 ? 1 : 0) : 0,
        trueFalse: selectedQuestionTypes.trueFalse ? baseCount + (remainder > 1 ? 1 : 0) : 0,
        fillBlank: selectedQuestionTypes.fillBlank ? baseCount + (remainder > 2 ? 1 : 0) : 0,
        essay: selectedQuestionTypes.essay ? baseCount : 0
      };
      
      // Build request with question types
      const requestData = {
        ...aiForm,
        questionTypes: distributedTypes,
        essayMinWords: selectedQuestionTypes.essay ? essayMinWords : undefined
      };

      const response = await quizService.requestAIQuiz(requestData);
      toast.success('Quiz criado com sucesso! Já está disponível na sua biblioteca.');
      handleClose();
      
      // Call the callback to refresh the quiz list
      if (onQuizCreated) {
        onQuizCreated();
      }
    } catch (error: any) {
      console.error('Error submitting AI quiz request:', error);
      
      // Handle quiz limit error with upgrade prompt
      if (error.response?.data?.code === 'QUIZ_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error('Você atingiu o limite de 3 quizzes no plano gratuito. Faça upgrade para criar mais quizzes!');
        }
      }
      // Handle quiz question limit error
      else if (error.response?.data?.code === 'QUIZ_QUESTION_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error(error.response?.data?.message || 'Limite de questões atingido. Faça upgrade para Premium!');
        }
      }
      else {
        toast.error(error.response?.data?.message || 'Erro ao criar quiz com IA. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeckBasedQuiz = () => {
    setStep('select-deck');
  };

  const handleDeckSelected = (deckId: string) => {
    handleClose();
    navigate(`/quizzes/create?type=deck-based&deckId=${deckId}`);
  };

  const handleCustomQuiz = () => {
    setStep('custom-basic-info');
  };

  const handleFileUpload = () => {
    setShowFileUploadModal(true);
  };

  const handleSearchQuizzes = () => {
    handleClose();
    navigate('/search?type=quizzes');
  };

  const handleFileUploadSubmit = async (formData: FormData) => {
    try {
      const result = await quizService.createQuizFromFile(formData);
      toast.success('Quiz criado com sucesso a partir do arquivo!');
      setShowFileUploadModal(false);
      handleClose();
      if (onQuizCreated) {
        onQuizCreated();
      }
      return result;
    } catch (error: any) {
      console.error('Error creating quiz from file:', error);
      
      // Handle quiz limit error with upgrade prompt
      if (error.response?.data?.code === 'QUIZ_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error('Você atingiu o limite de 3 quizzes no plano gratuito. Faça upgrade para criar mais quizzes!');
        }
      }
      // Handle quiz question limit error
      else if (error.response?.data?.code === 'QUIZ_QUESTION_LIMIT_REACHED') {
        if (onUpgradePrompt) {
          onUpgradePrompt();
        } else {
          toast.error(error.response?.data?.message || 'Limite de questões atingido. Faça upgrade para Premium!');
        }
      }
      
      throw error;
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !customQuizTags.includes(newTag.trim())) {
      setCustomQuizTags([...customQuizTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setCustomQuizTags(customQuizTags.filter(tag => tag !== tagToRemove));
  };

  const handleCustomQuizNext = async () => {
    if (!customQuizTitle.trim()) {
      toast.error('Por favor, insira um título para o quiz');
      return;
    }

    // Check quiz limit
    if (!checkLimit('quiz', quizCount)) {
      if (onUpgradePrompt) onUpgradePrompt();
      return;
    }
    
    try {
      setLoading(true);
      
      // Create the quiz with basic info
      const quizData = {
        title: customQuizTitle,
        description: customQuizDescription,
        type: 'custom' as const,
        questions: [], // Empty questions array for now
        tags: customQuizTags,
        settings: {
          randomizeQuestions: false,
          randomizeAnswers: true,
          showExplanations: true,
          allowRetries: true
        }
      };
      
      await quizService.createQuiz(quizData);
      
      toast.success('Quiz criado com sucesso!');
      handleClose();
      
      // Call the callback to refresh the quiz list
      if (onQuizCreated) {
        onQuizCreated();
      }
      
      navigate('/quizzes');
    } catch (error: any) {
      console.error('Error creating quiz:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('choice');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-4 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal content */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-3 pt-4 pb-3 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
          
          {/* Header */}
          <div className="flex items-start justify-between mb-4 sm:mb-6">
            <div className="flex items-center space-x-2">
              {(step === 'select-deck' || step === 'custom-basic-info' || step === 'ai' || step === 'file') && (
                <button
                  onClick={handleBack}
                  className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 mr-2"
                >
                  <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              )}
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 pr-2 leading-tight">
                {step === 'choice' && 'Como deseja criar seu quiz?'}
                {step === 'select-deck' && 'Selecione um Deck'}
                {step === 'custom-basic-info' && 'Informações Básicas'}
                {step === 'ai' && 'Criar Quiz com IA'}
                {step === 'file' && 'Criar Quiz a partir de Arquivo'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Step: Choice */}
          {step === 'choice' && (
            <div className="space-y-3 sm:space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                Escolha como gostaria de criar seu novo quiz de estudos:
              </p>

              {/* AI Quiz Option */}
              <button
                onClick={handleAIQuiz}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                      Criar com IA
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Nossa IA criará questões personalizadas baseadas no seu tópico de interesse
                    </p>
                  </div>
                </div>
              </button>

              {/* File Upload Option */}
              <button
                onClick={handleFileUpload}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <DocumentArrowUpIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-300">
                      Upload de Arquivo
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Envie até 5 arquivos (PDF, DOCX ou TXT) e a IA criará questões automaticamente
                    </p>
                  </div>
                </div>
              </button>

              {/* Search Quizzes Option */}
              <button
                onClick={handleSearchQuizzes}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-green-300 dark:border-green-600 rounded-lg hover:border-green-500 dark:hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <MagnifyingGlassIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-700 dark:group-hover:text-green-300">
                      Explorar Quizzes
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Copie quizzes compartilhados pela comunidade
                    </p>
                  </div>
                </div>
              </button>

              {/* Custom Quiz Option */}
              <button
                onClick={handleCustomQuiz}
                className="w-full p-4 sm:p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-500 dark:hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <PencilIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                      Quiz Personalizado
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Crie perguntas personalizadas do zero com controle total
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step: Select Deck */}
          {step === 'select-deck' && (
            <div className="space-y-3 sm:space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base">
                Escolha um deck para criar o quiz:
              </p>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : decks.length === 0 ? (
                <div className="text-center py-8">
                  <RectangleStackIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Você ainda não tem decks criados
                  </p>
                  <button
                    onClick={() => navigate('/decks')}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Criar Deck
                  </button>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {decks.map((deck) => (
                    <button
                      key={deck._id}
                      onClick={() => handleDeckSelected(deck._id)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 group text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-700 dark:group-hover:text-purple-300">
                            {deck.name}
                          </h4>
                          {deck.description && (
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                              {deck.description}
                            </p>
                          )}
                          <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>{deck.cardCount || 0} cards</span>
                          </div>
                        </div>
                        <RectangleStackIcon className="h-6 w-6 text-gray-400 dark:text-gray-500 group-hover:text-purple-600 dark:group-hover:text-purple-400 ml-4 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step: Custom Quiz Basic Info */}
          {step === 'custom-basic-info' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="quiz-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  id="quiz-title"
                  type="text"
                  value={customQuizTitle}
                  onChange={(e) => setCustomQuizTitle(e.target.value)}
                  placeholder="Digite o título do quiz"
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="quiz-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  id="quiz-description"
                  value={customQuizDescription}
                  onChange={(e) => setCustomQuizDescription(e.target.value)}
                  placeholder="Descrição opcional do quiz"
                  rows={4}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none text-sm sm:text-base"
                />
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="quiz-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex space-x-2">
                  <input
                    id="quiz-tags"
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Adicionar tag"
                    className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="flex-shrink-0 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors border border-gray-300 dark:border-gray-600"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Tag list */}
                {customQuizTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {customQuizTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 hover:text-purple-600 dark:hover:text-purple-400"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Create Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleCustomQuizNext}
                  disabled={!customQuizTitle.trim() || loading}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm sm:text-base flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Criando...</span>
                    </>
                  ) : (
                    <span>Criar Quiz</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step: AI Form */}
          {step === 'ai' && (
            <form onSubmit={handleAISubmit} className="space-y-5 sm:space-y-6">
              {/* Progress Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  {[1, 2, 3, 4, 5].map((stepNum) => (
                    <React.Fragment key={stepNum}>
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                            aiSubStep > stepNum
                              ? 'bg-green-500 text-white'
                              : aiSubStep === stepNum
                              ? 'bg-purple-600 text-white ring-4 ring-purple-200 dark:ring-purple-800'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {aiSubStep > stepNum ? '✓' : stepNum}
                        </div>
                        <span className={`text-xs mt-1 text-center hidden sm:block ${
                          aiSubStep >= stepNum ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-gray-400'
                        }`}>
                          {stepNum === 1 ? 'Tópico' : stepNum === 2 ? 'Detalhes' : stepNum === 3 ? 'Tipos' : stepNum === 4 ? 'Config' : 'Revisão'}
                        </span>
                      </div>
                      {stepNum < 5 && (
                        <div className={`h-1 flex-1 mx-1 rounded transition-all duration-300 ${
                          aiSubStep > stepNum ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="sm:hidden text-center">
                  <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                    {aiSubStep === 1 ? 'Tópico' : aiSubStep === 2 ? 'Detalhes' : aiSubStep === 3 ? 'Tipos de Questões' : aiSubStep === 4 ? 'Configuração' : 'Revisão'}
                  </span>
                </div>
              </div>

              {/* Step 1: Topic */}
              {aiSubStep === 1 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <SparklesIcon className="h-12 w-12 text-purple-500 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Sobre o que será o quiz?
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Digite o tema principal do seu quiz
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="ai-topic" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Tópico *
                    </label>
                    <input
                      id="ai-topic"
                      type="text"
                      value={aiForm.topic}
                      onChange={(e) => setAIForm({ ...aiForm, topic: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!aiForm.topic.trim()) {
                            toast.error('Por favor, insira um tópico');
                            return;
                          }
                          setAiSubStep(aiSubStep + 1);
                        }
                      }}
                      placeholder="Ex: JavaScript Avançado, História do Brasil, Matemática Financeira"
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base transition-all"
                      autoFocus
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'JavaScript Avançado' })}
                        className="px-3 py-1.5 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        JavaScript
                      </button>
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'História do Brasil' })}
                        className="px-3 py-1.5 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        História
                      </button>
                      <button
                        type="button"
                        onClick={() => setAIForm({ ...aiForm, topic: 'Matemática Financeira' })}
                        className="px-3 py-1.5 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        Matemática
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Description */}
              {aiSubStep === 2 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <PencilIcon className="h-12 w-12 text-purple-500 mx-auto mb-3" />
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Descreva o quiz
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Quanto mais detalhes, melhores serão as questões
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="ai-description" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Descrição *
                    </label>
                    <textarea
                      id="ai-description"
                      rows={5}
                      value={aiForm.description}
                      onChange={(e) => setAIForm({ ...aiForm, description: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          if (aiForm.description.length < 20) {
                            toast.error('A descrição deve ter pelo menos 20 caracteres');
                            return;
                          }
                          setAiSubStep(aiSubStep + 1);
                        }
                      }}
                      placeholder="Descreva o que este quiz deve cobrir. Seja específico!"
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-base transition-all"
                      autoFocus
                    />
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                      <span>Mínimo 20 caracteres</span>
                      <span className={aiForm.description.length >= 20 ? 'text-green-600 dark:text-green-400' : ''}>
                        {aiForm.description.length} caracteres
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Question Types */}
              {aiSubStep === 3 && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg mb-4">
                      <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Tipos de Questões
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Selecione os formatos que deseja no quiz
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Multiple Choice */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.multipleChoice
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-xl shadow-blue-500/30 dark:shadow-blue-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.multipleChoice
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-blue-50 dark:bg-blue-900/20'
                        }`}>
                          📝
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.multipleChoice
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                          }`}>
                            Múltipla Escolha
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.multipleChoice
                              ? 'text-blue-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Questões com 4 opções de resposta
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.multipleChoice}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, multipleChoice: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* True/False */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.trueFalse
                          ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-xl shadow-green-500/30 dark:shadow-green-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.trueFalse
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-green-50 dark:bg-green-900/20'
                        }`}>
                          ✓✗
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.trueFalse
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400'
                          }`}>
                            Verdadeiro ou Falso
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.trueFalse
                              ? 'text-green-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Questões de verdadeiro ou falso
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.trueFalse}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, trueFalse: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* Fill in the Blank */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.fillBlank
                          ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-xl shadow-amber-500/30 dark:shadow-amber-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold transition-all ${
                          selectedQuestionTypes.fillBlank
                            ? 'bg-white/20 backdrop-blur-sm text-white'
                            : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                        }`}>
                          ___
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.fillBlank
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-amber-600 dark:group-hover:text-amber-400'
                          }`}>
                            Preencher Lacuna
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.fillBlank
                              ? 'text-amber-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Completar frases com palavras faltando
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.fillBlank}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, fillBlank: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>

                    {/* Essay */}
                    <label 
                      className={`group relative rounded-2xl p-5 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedQuestionTypes.essay
                          ? 'bg-gradient-to-br from-purple-500 to-violet-600 shadow-xl shadow-purple-500/30 dark:shadow-purple-500/20'
                          : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                          selectedQuestionTypes.essay
                            ? 'bg-white/20 backdrop-blur-sm'
                            : 'bg-purple-50 dark:bg-purple-900/20'
                        }`}>
                          📄
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-lg font-bold mb-1 transition-colors ${
                            selectedQuestionTypes.essay
                              ? 'text-white'
                              : 'text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400'
                          }`}>
                            Dissertativa
                          </h5>
                          <p className={`text-sm transition-colors ${
                            selectedQuestionTypes.essay
                              ? 'text-purple-50'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            Respostas abertas avaliadas por IA
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selectedQuestionTypes.essay}
                            onChange={(e) => setSelectedQuestionTypes({ ...selectedQuestionTypes, essay: e.target.checked })}
                            className="w-6 h-6 rounded-lg cursor-pointer transition-all"
                          />
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 4: Configuration */}
              {aiSubStep === 4 && (
                <div className="space-y-5 animate-fadeIn">
                  <div className="text-center mb-6">
                    <svg className="h-12 w-12 text-purple-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Configure seu quiz
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Ajuste quantidade, dificuldade e idioma
                    </p>
                  </div>

                  {/* Question Count Slider */}
                  <div>
                    <label htmlFor="ai-question-count" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Número de Questões: <span className="text-purple-600 dark:text-purple-400 font-bold">{aiForm.questionCount}</span>
                      {subscription && subscription.subscriptionTier === 'free' && !isAndroidWebView && (
                        <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                          (máx. 15 para <a href="/pricing" className="underline hover:text-amber-700 dark:hover:text-amber-300">plano gratuito</a>)
                        </span>
                      )}
                    </label>
                    <input
                      id="ai-question-count"
                      type="range"
                      min="5"
                      max={subscription?.subscriptionTier === 'free' ? '15' : '30'}
                      value={aiForm.questionCount}
                      onChange={(e) => setAIForm({ ...aiForm, questionCount: parseInt(e.target.value) })}
                      className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <span>5</span>
                      <span>{subscription?.subscriptionTier === 'free' ? '15' : '30'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Dificuldade
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'beginner', label: 'Iniciante', icon: '🌱' },
                          { value: 'intermediate', label: 'Intermediário', icon: '🌿' },
                          { value: 'advanced', label: 'Avançado', icon: '🌳' }
                        ].map((diff) => (
                          <button
                            key={diff.value}
                            type="button"
                            onClick={() => setAIForm({ ...aiForm, difficulty: diff.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              aiForm.difficulty === diff.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{diff.icon}</span>
                            {diff.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Idioma
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'portuguese', label: 'Português', flag: '🇧🇷' },
                          { value: 'english', label: 'English', flag: '🇺🇸' },
                          { value: 'spanish', label: 'Español', flag: '🇪🇸' }
                        ].map((lang) => (
                          <button
                            key={lang.value}
                            type="button"
                            onClick={() => setAIForm({ ...aiForm, language: lang.value as any })}
                            className={`w-full px-4 py-3 rounded-lg border-2 font-medium text-sm text-left transition-all flex items-center ${
                              aiForm.language === lang.value
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <span className="text-xl mr-3">{lang.flag}</span>
                            {lang.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="ai-additional-info" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Informações Adicionais (Opcional)
                    </label>
                    <textarea
                      id="ai-additional-info"
                      rows={3}
                      value={aiForm.additionalInfo}
                      onChange={(e) => setAIForm({ ...aiForm, additionalInfo: e.target.value })}
                      placeholder="Requisitos especiais, foco específico, etc."
                      className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm transition-all"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Review */}
              {aiSubStep === 5 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="text-center mb-6">
                    <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Tudo pronto!
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Revise as informações antes de criar seu quiz
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-purple-50 dark:from-gray-800 dark:to-purple-900/20 rounded-xl p-5 space-y-4 border-2 border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Tópico</h5>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{aiForm.topic}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(1)}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 p-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex items-start justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex-1">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Descrição</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{aiForm.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(2)}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 p-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tipos de Questões</h5>
                        <button
                          type="button"
                          onClick={() => setAiSubStep(3)}
                          className="text-purple-600 dark:text-purple-400 hover:text-purple-700 p-1"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedQuestionTypes.multipleChoice && (
                          <div className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            📝 Múltipla Escolha
                          </div>
                        )}
                        {selectedQuestionTypes.trueFalse && (
                          <div className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            ✓✗ V ou F
                          </div>
                        )}
                        {selectedQuestionTypes.fillBlank && (
                          <div className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            ___ Lacuna
                          </div>
                        )}
                        {selectedQuestionTypes.essay && (
                          <div className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded flex items-center">
                            <input type="checkbox" checked disabled className="mr-1.5 w-3 h-3" />
                            📄 Dissertativa
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Total Questões</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{aiForm.questionCount} questões</p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Dificuldade</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {aiForm.difficulty === 'beginner' ? '🌱 Iniciante' : aiForm.difficulty === 'intermediate' ? '🌿 Intermediário' : '🌳 Avançado'}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Idioma</h5>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {aiForm.language === 'portuguese' ? '🇧🇷 Português' : aiForm.language === 'spanish' ? '🇪🇸 Español' : '🇺🇸 English'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiSubStep(4)}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 font-medium text-left flex items-center"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        Editar configuração
                      </button>
                    </div>

                    {aiForm.additionalInfo && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Info Adicional</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{aiForm.additionalInfo}</p>
                      </div>
                    )}
                  </div>

                  {!loading && (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-5 w-5 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1 text-sm">
                          <p className="text-purple-700 dark:text-purple-200 font-semibold mb-1">
                            💡 Quiz gerado automaticamente
                          </p>
                          <p className="text-purple-600 dark:text-purple-300">
                            Nossa IA criará questões personalizadas baseadas nas suas especificações.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Bar - Show when submitting */}
              {loading && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                      <span className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                        Criando seu quiz com IA...
                      </span>
                    </div>
                    <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                      ⏱️ ~30-60s
                    </span>
                  </div>
                  
                  <div className="w-full bg-purple-100 dark:bg-purple-800 rounded-full h-2.5 overflow-hidden">
                    <div className="h-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 dark:from-purple-400 dark:to-indigo-400 rounded-full animate-pulse"></div>
                  </div>
                  
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-3 text-center">
                    Nossa IA está analisando seu tópico e criando questões personalizadas...
                  </p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    if (aiSubStep > 1) {
                      setAiSubStep(aiSubStep - 1);
                    } else {
                      handleBack();
                    }
                  }}
                  disabled={loading}
                  className="flex-1 px-5 py-3.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {aiSubStep === 1 ? 'Voltar' : 'Anterior'}
                </button>

                {aiSubStep < 5 ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (aiSubStep === 1 && !aiForm.topic.trim()) {
                        toast.error('Por favor, insira um tópico');
                        return;
                      }
                      if (aiSubStep === 2 && aiForm.description.length < 20) {
                        toast.error('A descrição deve ter pelo menos 20 caracteres');
                        return;
                      }
                      if (aiSubStep === 3) {
                        const hasSelectedType = selectedQuestionTypes.multipleChoice || selectedQuestionTypes.trueFalse || 
                                               selectedQuestionTypes.fillBlank || selectedQuestionTypes.essay;
                        if (!hasSelectedType) {
                          toast.error('Selecione pelo menos um tipo de questão');
                          return;
                        }
                      }
                      setAiSubStep(aiSubStep + 1);
                    }}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-purple-500/30"
                  >
                    Continuar →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-5 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl transition-all text-base font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Gerando...</span>
                      </div>
                    ) : (
                      <span className="flex items-center justify-center">
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        Gerar Quiz com IA
                      </span>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* File Upload Modal */}
      <FileUploadModal
        isOpen={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        type="quiz"
        onSubmit={handleFileUploadSubmit}
        subscription={subscription}
      />
    </div>
  );
};

export default QuizCreationModal;